import { notify } from './react-toastify.function.js';

/**
 * Validates text input to prevent special characters like dots, semicolons, etc.
 * @param {string} value - The input value to validate
 * @param {string} fieldName - Name of the field for error message (default: "Text")
 * @returns {boolean} - Whether the input is valid
 */
export const validateTextInput = (value, fieldName = 'Text') => {
  // Define allowed characters: letters, numbers, spaces, and common punctuation
  const allowedPattern = /^[a-zA-Z0-9\s.,!?\-_()@#$%&*+=:;'"<>/\\|`~[\]{}]*$/;

  if (!allowedPattern.test(value)) {
    const message = `${fieldName} contains invalid characters. Please use only letters, numbers, and common symbols.`;
    notify('error', message);
    return false;
  }

  return true;
};

/**
 * Validates text input and prevents specific problematic patterns
 * @param {string} value - The input value to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {boolean} - Whether the input is valid
 */
export const validateAndPreventSpecialChars = (value, fieldName = 'Text') => {
  // Only allow letters, numbers, and spaces
  const allowedPattern = /^[a-zA-Z0-9\s]*$/;

  if (!allowedPattern.test(value)) {
    const message = `${fieldName} contains invalid characters. Please use only letters, numbers, and spaces.`;
    notify('error', message);
    return false;
  }

  // Check for problematic patterns like multiple dots, semicolons, etc.
  const problematicPatterns = [
    /\.{2,}/, // Multiple consecutive dots
    /;{2,}/, // Multiple consecutive semicolons
    /\/{2,}/, // Multiple consecutive slashes
    /\\{2,}/, // Multiple consecutive backslashes
    /:{2,}/, // Multiple consecutive colons
    /,{2,}/, // Multiple consecutive commas
    /!{2,}/, // Multiple consecutive exclamation marks
    /\?{2,}/, // Multiple consecutive question marks
    /-{2,}/, // Multiple consecutive hyphens
    /_{2,}/, // Multiple consecutive underscores
    /\({2,}/, // Multiple consecutive opening parentheses
    /\){2,}/, // Multiple consecutive closing parentheses
    /\[{2,}/, // Multiple consecutive opening brackets
    /\]{2,}/, // Multiple consecutive closing brackets
    /"{2,}/, // Multiple consecutive quotes
    /'{2,}/, // Multiple consecutive single quotes
    /`{2,}/, // Multiple consecutive backticks
    /~{2,}/, // Multiple consecutive tildes
    /@{2,}/, // Multiple consecutive at symbols
    /#{2,}/, // Multiple consecutive hash symbols
    /%{2,}/, // Multiple consecutive percent symbols
    /&{2,}/, // Multiple consecutive ampersands
    /\*{2,}/, // Multiple consecutive asterisks
    /\+{2,}/, // Multiple consecutive plus signs
    /={2,}/, // Multiple consecutive equals signs
    /\|{2,}/, // Multiple consecutive pipes
    /<{2,}/, // Multiple consecutive less than
    />{2,}/, // Multiple consecutive greater than
  ];

  // Check for problematic patterns
  for (const pattern of problematicPatterns) {
    if (pattern.test(value)) {
      const message = `${fieldName} contains invalid character patterns. Please avoid repeating special characters.`;
      notify('error', message);
      return false;
    }
  }

  // Also check for mixed problematic characters
  const mixedProblematic = /[.;/\\:,!?\-_()@#$%&*+=`~[\]{}"'<>|]{3,}/;
  if (mixedProblematic.test(value)) {
    const message = `${fieldName} contains too many special characters. Please use them sparingly.`;
    notify('error', message);
    return false;
  }

  return true;
};

/**
 * Simple validation function that prevents dots and semicolons specifically
 * @param {string} value - The input value to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {boolean} - Whether the input is valid
 */
export const preventDotsAndSemicolons = (value, fieldName = 'Text') => {
  if (value.includes('.') || value.includes(';')) {
    const message = `${fieldName} cannot contain dots (.) or semicolons (;).`;
    notify('error', message);
    return false;
  }
  return true;
};

/**
 * Validates text input on change and prevents invalid characters
 * @param {Event} event - The input change event
 * @param {string} fieldName - Name of the field for error message
 * @param {Function} setValue - Function to update the input value
 * @param {string} previousValue - Previous valid value
 */
export const handleTextInputChange = (event, fieldName, setValue, previousValue = '') => {
  const newValue = event.target.value;

  // Validate the new value
  if (validateAndPreventSpecialChars(newValue, fieldName)) {
    setValue(newValue);
  } else {
    // Revert to previous value or clear the input
    event.target.value = previousValue;
    setValue(previousValue);
  }
};

/**
 * Validates text input on blur
 * @param {string} value - The input value to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {boolean} - Whether the input is valid
 */
export const validateOnBlur = (value, fieldName) => {
  return validateAndPreventSpecialChars(value, fieldName);
};

/**
 * Sanitizes input by removing problematic characters
 * @param {string} value - The input value to sanitize
 * @returns {string} - Sanitized value
 */
export const sanitizeTextInput = (value) => {
  return value
    .replace(/\.{2,}/g, '.') // Replace multiple dots with single dot
    .replace(/;{2,}/g, ';') // Replace multiple semicolons with single semicolon
    .replace(/\/{2,}/g, '/') // Replace multiple slashes with single slash
    .replace(/\\{2,}/g, '\\') // Replace multiple backslashes with single backslash
    .replace(/:{2,}/g, ':') // Replace multiple colons with single colon
    .replace(/,{2,}/g, ',') // Replace multiple commas with single comma
    .replace(/!{2,}/g, '!') // Replace multiple exclamation marks with single
    .replace(/\?{2,}/g, '?') // Replace multiple question marks with single
    .replace(/-{2,}/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single underscore
    .replace(/\({2,}/g, '(') // Replace multiple opening parentheses with single
    .replace(/\){2,}/g, ')') // Replace multiple closing parentheses with single
    .replace(/\[{2,}/g, '[') // Replace multiple opening brackets with single
    .replace(/\]{2,}/g, ']') // Replace multiple closing brackets with single
    .replace(/"{2,}/g, '"') // Replace multiple quotes with single quote
    .replace(/'{2,}/g, "'") // Replace multiple single quotes with single
    .replace(/`{2,}/g, '`') // Replace multiple backticks with single backtick
    .replace(/~{2,}/g, '~') // Replace multiple tildes with single tilde
    .replace(/@{2,}/g, '@') // Replace multiple at symbols with single
    .replace(/#{2,}/g, '#') // Replace multiple hash symbols with single
    .replace(/%{2,}/g, '%') // Replace multiple percent symbols with single
    .replace(/&{2,}/g, '&') // Replace multiple ampersands with single
    .replace(/\*{2,}/g, '*') // Replace multiple asterisks with single
    .replace(/\+{2,}/g, '+') // Replace multiple plus signs with single
    .replace(/={2,}/g, '=') // Replace multiple equals signs with single
    .replace(/\|{2,}/g, '|') // Replace multiple pipes with single pipe
    .replace(/<{2,}/g, '<') // Replace multiple less than with single
    .replace(/>{2,}/g, '>'); // Replace multiple greater than with single
};
